<template>
  <div class="asp-wrap">
    <!-- Compound pill: the label opens the full drawer (unchanged behaviour); the chevron on the
         right only shows/hides the quick-ask card below. Both live inside one gradient pill. -->
    <div class="asp-pill" :class="{ 'asp-pill--open': cardShown && isDesktop }">
      <button
        type="button"
        class="asp-pill-main"
        :title="$t('Assistant.OpenChat')"
        @click="openDrawer"
      >
        <i class="fas fa-wand-magic-sparkles asp-pill-icon"></i>
        <span class="asp-pill-text-full">{{ $t('Assistant.AskAI') }}</span>
        <span class="asp-pill-text-short">{{ $t('Assistant.AskAIShort') }}</span>
      </button>
      <button
        v-if="isDesktop"
        type="button"
        class="asp-pill-toggle"
        :aria-label="cardShown ? $t('Assistant.HidePeek') : $t('Assistant.ShowPeek')"
        @click="toggleCard"
      >
        <i class="fas" :class="cardShown ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
      </button>
    </div>

    <transition name="asp-pop">
      <div v-if="cardShown && isDesktop" class="asp-card">
        <div class="asp-field">
          <input
            ref="input"
            v-model="text"
            class="asp-input"
            :class="{ 'asp-input--fading': fading }"
            :placeholder="placeholder"
            @keydown.enter.prevent="submit"
            @focus="focused = true"
            @blur="focused = false"
          />
          <button
            type="button"
            class="asp-send"
            :aria-label="$t('Assistant.Send')"
            @click="submit"
          >
            <i class="fas fa-arrow-up"></i>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { useAssistantChat } from '@/views/assistant/useAssistantChat'
import { useAssistantDrawer } from '@/composables/useAssistantDrawer'

const HIDDEN_KEY = 'assistant_peek_hidden'
const DESKTOP_MQ = '(min-width: 992px)'
const ROTATE_MS = 5000
const FADE_MS = 250

export default {
  name: 'AssistantPeek',
  setup() {
    return { chat: useAssistantChat(), drawer: useAssistantDrawer() }
  },
  data() {
    return {
      cardShown: true,
      text: '',
      focused: false,
      fading: false,
      hintIndex: 0,
      isDesktop: true,
    }
  },
  computed: {
    hints() {
      const arr = this.$tm('Assistant.PeekHints')
      if (!Array.isArray(arr)) return []
      return arr.map((h) => (typeof h === 'string' ? h : (this.$rt ? this.$rt(h) : String(h))))
    },
    placeholder() {
      return this.hints[this.hintIndex] || this.$t('Assistant.InputPlaceholder')
    },
  },
  mounted() {
    this.mq = window.matchMedia(DESKTOP_MQ)
    this.isDesktop = this.mq.matches
    this.mqHandler = (e) => { this.isDesktop = e.matches }
    this.mq.addEventListener('change', this.mqHandler)

    // Shown by default to pull attention to the assistant; stays hidden once the user dismisses it.
    this.cardShown = !this.isHidden()

    this.rotateTimer = setInterval(this.rotateHint, ROTATE_MS)
  },
  beforeUnmount() {
    clearInterval(this.rotateTimer)
    this.mq?.removeEventListener('change', this.mqHandler)
  },
  methods: {
    isHidden() {
      try { return localStorage.getItem(HIDDEN_KEY) === '1' } catch (e) { return false }
    },
    setHidden(hidden) {
      try {
        if (hidden) localStorage.setItem(HIDDEN_KEY, '1')
        else localStorage.removeItem(HIDDEN_KEY)
      } catch (e) { /* private mode — non-fatal */ }
    },
    openDrawer() {
      this.drawer.toggle()
    },
    toggleCard() {
      this.cardShown = !this.cardShown
      this.setHidden(!this.cardShown)
    },
    async submit() {
      const text = this.text.trim()
      if (!text || this.chat.busy.value) return
      this.text = ''
      this.drawer.open()
      await this.chat.sendMessage(text)
    },
    rotateHint() {
      if (!this.cardShown || this.focused || this.text || this.hints.length < 2) return
      this.fading = true
      setTimeout(() => {
        this.hintIndex = (this.hintIndex + 1) % this.hints.length
        this.fading = false
      }, FADE_MS)
    },
  },
}
</script>

<style scoped>
.asp-wrap { position: relative; display: inline-flex; }

/* Gradient pill holding two click targets (open drawer / toggle card). */
.asp-pill {
  display: inline-flex;
  align-items: center;
  height: 32px;
  border-radius: 16px;
  background: #7b2ff7;
  box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.25);
  overflow: hidden;
  transition: box-shadow 0.15s;
}

/* When the card is open, the pill squares its bottom corners and drops the ring so it fuses with
   the card below into one shape. */
.asp-pill--open {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  box-shadow: none;
}

.asp-pill-main {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 100%;
  padding: 0 8px 0 14px;
  border: none;
  background: transparent;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s;
}
.asp-pill-main .fas { font-size: 13px; }
.asp-pill-main:hover { background: rgba(255, 255, 255, 0.16); }

.asp-pill-text-short { display: none; }

/* On narrow screens there's no room for icon + full label, so drop the icon and
   shorten the text to just "AI". */
@media (max-width: 575.98px) {
  .asp-pill-icon { display: none; }
  .asp-pill-text-full { display: none; }
  .asp-pill-text-short { display: inline; }
  .asp-pill-main { padding: 0 10px; gap: 0; }
}

.asp-pill-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 0 11px 0 8px;
  border: none;
  border-left: 1px solid rgba(255, 255, 255, 0.22);
  background: transparent;
  color: rgba(255, 255, 255, 0.9);
  font-size: 11px;
  cursor: pointer;
  transition: background 0.15s;
}
.asp-pill-toggle:hover { background: rgba(255, 255, 255, 0.16); color: #fff; }

/* Quick-ask card: white surface with a brand-purple outline, floating below the pill.
   Absolute so the navbar row never shifts when it shows/hides. */
.asp-card {
  position: absolute;
  top: 32px;
  right: 0;
  /* Below Bootstrap's $zindex-dropdown (1000) so navbar dropdowns (Profile, etc.) open above the
     card. Still floats over page content because the whole navbar sits in its own sticky layer. */
  z-index: 900;
  width: 360px;
  /* Same flat purple as the pill so pill + card read as one continuous shape (a gradient would
     render per-box and visibly break at the seam). */
  background: #7b2ff7;
  /* Top-right corner squared so it butts up flush against the pill's squared bottom-right. */
  border-radius: 14px 0 14px 14px;
  box-shadow: 0 10px 28px rgba(109, 40, 217, 0.3);
  padding: 8px;
}

.asp-field {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border-radius: 12px;
  padding: 4px 4px 4px 12px;
}

.asp-input {
  flex: 1;
  min-width: 0;
  height: 34px;
  padding: 0;
  border: none;
  background: transparent;
  font-size: 13px;
  color: #1a1a2e;
  transition: opacity 0.25s ease;
}
.asp-input:focus { outline: none; }
.asp-input::placeholder { color: #8a8aa0; }
.asp-input--fading { opacity: 0; }

.asp-send {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 9px;
  background: linear-gradient(135deg, #7b2ff7, #6d28d9);
  color: #fff;
  font-size: 15px;
  cursor: pointer;
}
.asp-send:hover { filter: brightness(1.08); }

.asp-pop-enter-active, .asp-pop-leave-active { transition: opacity 0.18s ease, transform 0.18s ease; }
.asp-pop-enter-from, .asp-pop-leave-to { opacity: 0; transform: translateY(-6px); }
</style>
